import React, { useMemo } from "react";
import { Container } from "react-bootstrap";
import PageLoading from "components/PageLoading";
import { Section, type DocumentoAgrupado } from "./VisaoGeralBlocks";
import "./VisaoGeralTab.css";

type ArquivoEnviado = {
    nome?: string;
    nomeArquivo?: string;
    dataUpload?: string;
};

type Campo = {
    idCampo?: string;
    titulo?: string;
    resposta?: string;
    tipoCampo?: string;
    nomeArquivo?: string;
    alternativa?: Alternativa[];
};

type Alternativa = {
    titulo?: string;
    valor?: string;
    campo?: Campo[];
};

type Criterio = {
    titulo?: string;
    campo?: Campo[];
};

type Tema = {
    titulo?: string;
    criterio?: Criterio[];
};

type SecaoData = {
    tema?: Tema[];
};

type RespostaJson = {
    docsObrigatorios?: SecaoData[];
    docsBonus?: SecaoData[];
    docsBonusExtra?: SecaoData[];
    selectedOptionsObrigatorios?: Record<string, unknown>;
    selectedOptionsBonus?: Record<string, unknown>;
    selectedOptionsBonusExtra?: Record<string, unknown>;
};

type RespostaApi = {
    data?: {
        json?: string;
    };
};

type SindicatoApi = {
    data?: {
        cnpj?: string;
        nome?: string;
    };
};

type VisaoGeralTabProps = {
    resposta?: RespostaApi | null;
    sindicatoData?: SindicatoApi | null;
    documentos?: ArquivoEnviado[];
};

const encontrarArquivoMaisRecente = (
    nomeEsperado: string,
    arquivos: ArquivoEnviado[] = []
) => {
    const nomeBaseEsperado = String(nomeEsperado || "")
        .toLowerCase()
        .replace(/-v\d+/i, "")
        .replace(/\.(pdf|xlsx)/i, "")
        .trim();

    const relacionados = arquivos
        .filter((arquivo) => {
            const nomeReal = String(arquivo.nome || arquivo.nomeArquivo || "")
                .toLowerCase()
                .trim();

            return nomeReal.includes(nomeBaseEsperado);
        })
        .sort(
            (a, b) =>
                new Date(b.dataUpload || 0).getTime() -
                new Date(a.dataUpload || 0).getTime()
        );

    return relacionados[0];
};

const gerarMapasDeDocumentos = (
    docsEstrutura: SecaoData[] = [],
    arquivosEnviados: ArquivoEnviado[] = [],
    selectedOptions: Record<string, unknown> = {}
) => {
    const documentos: DocumentoAgrupado[] = [];
    let qtdAssociadosInformada = 0;

    docsEstrutura.forEach((secaoData) => {
        secaoData.tema?.forEach((tema, index) => {
            const titulosIgnorados = [
                "6. RECEITA OPERACIONAL ANUAL / ARRECADAÇÃO MÍNIMA MENSAL",
                "7. EVOLUÇÃO RECEITA OPERACIONAL",
                "8. 80% DE RETENÇÃO DE ASSOCIADOS",
                "9. EVOLUÇÃO DO ASSOCIATIVISMO",
            ];

            if (tema.titulo && titulosIgnorados.includes(tema.titulo)) return;

            const titulo = tema.titulo || `Documento ${index + 1}`;
            const selected = selectedOptions?.[titulo] as
                | string
                | { resposta?: string; observacao?: string }
                | undefined;

            const respostaSelecionada =
                typeof selected === "string" ? selected : selected?.resposta || "";

            const observacao =
                typeof selected === "object" && selected
                    ? selected.observacao || ""
                    : "";

            const conteudo: (string | React.ReactElement)[] = [];

            tema.criterio?.forEach((criterio) => {
                if (criterio.titulo?.includes("Quantas empresas associadas")) {
                    const campoRadio = criterio.campo?.find(
                        (c) => c.tipoCampo === "radio"
                    );

                    qtdAssociadosInformada = parseInt(campoRadio?.resposta || "0", 10) || 0;

                    conteudo.push(`${criterio.titulo}`);
                    conteudo.push(`Resposta: ${campoRadio?.resposta || "Não informado"}`);
                    return;
                }

                if (criterio.titulo?.startsWith("Tipo do documento")) {
                    const campoRadio = criterio.campo?.find(
                        (c) => c.tipoCampo === "radio"
                    );

                    conteudo.push(`${criterio.titulo}`);
                    conteudo.push(`Resposta: ${campoRadio?.resposta || "Não informado"}`);
                    return;
                }

                if (/Associado \d+/.test(criterio.titulo || "")) {
                    const numeroAssociado = parseInt(
                        criterio.titulo?.match(/\d+/)?.[0] || "0",
                        10
                    );

                    if (numeroAssociado > qtdAssociadosInformada) return;

                    conteudo.push(`${criterio.titulo}`);

                    criterio.campo?.forEach((campo) => {
                        const label = campo.titulo || "Campo";
                        const resposta = campo.resposta || "Não preenchido";

                        if (
                            respostaSelecionada.toUpperCase() === "NÃO" &&
                            (campo.tipoCampo === "pdf" || campo.tipoCampo === "xlsx")
                        ) {
                            return;
                        }

                        if (campo.tipoCampo === "pdf" && campo.nomeArquivo) {
                            const arquivoEncontrado = encontrarArquivoMaisRecente(
                                campo.nomeArquivo,
                                arquivosEnviados
                            );

                            if (arquivoEncontrado) {
                                conteudo.push(
                                    "Arquivo: " +
                                    (arquivoEncontrado.nomeArquivo || arquivoEncontrado.nome || "")
                                );
                            } else {
                                conteudo.push(
                                    "Arquivo: Nenhum documento anexado para este campo."
                                );
                            }
                        } else {
                            if (label !== criterio.titulo) {
                                conteudo.push(`${label}: ${resposta}`);
                            } else {
                                conteudo.push(`${resposta}`);
                            }
                        }
                    });

                    return;
                }

                if (criterio?.titulo) {
                    conteudo.push(`${criterio.titulo}`);
                }

                criterio.campo?.forEach((campo, campoIndex) => {
                    const label = campo.titulo || "Resposta";
                    let resposta = campo.resposta || "Não preenchido";
                    let respostaNivel1 = "";
                    let respostaNivel2 = "";
                    let respostaNivel3 = "";

                    if (
                        tema.titulo === "1. AÇÃO INTERNACIONAL" &&
                        campo.idCampo === "tipo-acao-internacional" &&
                        campo.resposta
                    ) {
                        respostaNivel1 = campo.resposta;

                        if (
                            respostaNivel1 !== "" &&
                            respostaNivel1 !== "Certificado de origem"
                        ) {
                            campo.alternativa?.forEach((alt2) => {
                                if (alt2.titulo === respostaNivel1) {
                                    if (alt2.campo?.[0]?.resposta) {
                                        respostaNivel2 = alt2.campo[0].resposta || "";

                                        if (
                                            respostaNivel2 !== "Capacitação" &&
                                            respostaNivel2 !==
                                            "Estudos de inteligência em Negócios Internacionais"
                                        ) {
                                            alt2.campo[0].alternativa?.forEach((alt3) => {
                                                if (alt3.valor === respostaNivel2) {
                                                    respostaNivel3 = alt3.campo?.[0]?.resposta || "";

                                                    const campoPdf = alt3.campo?.find(
                                                        (c) => c.tipoCampo === "pdf"
                                                    );

                                                    if (campoPdf?.nomeArquivo) {
                                                        const arquivo = encontrarArquivoMaisRecente(
                                                            campoPdf.nomeArquivo,
                                                            arquivosEnviados
                                                        );

                                                        if (arquivo) {
                                                            conteudo.push(
                                                                "Arquivo: " +
                                                                (arquivo.nomeArquivo || arquivo.nome || "")
                                                            );
                                                        } else {
                                                            conteudo.push(
                                                                "Nenhum documento anexado para este campo."
                                                            );
                                                        }
                                                    }
                                                }
                                            });
                                        }
                                    }
                                }
                            });
                        } else {
                            resposta = respostaNivel1;

                            const alternativaSelecionada = campo.alternativa?.find(
                                (alt) => alt.titulo === respostaNivel1
                            );

                            if (alternativaSelecionada?.campo) {
                                const campoPdf = alternativaSelecionada.campo.find(
                                    (c) => c.tipoCampo === "pdf"
                                );

                                if (campoPdf?.nomeArquivo) {
                                    const arquivo = encontrarArquivoMaisRecente(
                                        campoPdf.nomeArquivo,
                                        arquivosEnviados
                                    );

                                    if (arquivo) {
                                        conteudo.push(
                                            "Arquivo: " + (arquivo.nomeArquivo || arquivo.nome || "")
                                        );
                                    } else {
                                        conteudo.push(
                                            "Nenhum documento anexado para este campo."
                                        );
                                    }
                                }
                            }
                        }

                        if (
                            respostaNivel1 !== "Certificado de origem" &&
                            respostaNivel2 === ""
                        ) {
                            conteudo.push("Critério incompleto.");
                        } else if (
                            respostaNivel2 ===
                            "Participação em Missões Internacionais organizadas pelo CIN" &&
                            respostaNivel3 === ""
                        ) {
                            conteudo.push("Critério incompleto.");
                        }
                    }

                    if (respostaNivel2 !== "") {
                        resposta = resposta + " → " + respostaNivel2;
                    }

                    if (respostaNivel3 !== "") {
                        resposta = resposta + " → " + respostaNivel3;
                    }

                    if (campo.tipoCampo === "pdf" || campo.tipoCampo === "xlsx") {
                        const arquivoEncontrado = campo.nomeArquivo
                            ? encontrarArquivoMaisRecente(campo.nomeArquivo, arquivosEnviados)
                            : null;

                        if (arquivoEncontrado) {
                            conteudo.push(
                                "Arquivo: " +
                                (arquivoEncontrado.nomeArquivo || arquivoEncontrado.nome || "")
                            );
                        } else if (
                            !arquivoEncontrado &&
                            criterio.campo?.[0]?.resposta === "NÃO"
                        ) {
                            // não exibe nada quando a resposta for NÃO
                        } else {
                            conteudo.push("Nenhum documento anexado para este campo.");
                        }
                    } else {
                        const isCampoDuplicadoDoTitulo =
                            campoIndex === 0 &&
                            criterio.titulo &&
                            label.trim() === criterio.titulo.trim();

                        if (!isCampoDuplicadoDoTitulo) {
                            conteudo.push(`${label}: ${resposta}`);
                        }
                    }
                });
            });

            if (respostaSelecionada) {
                conteudo.push(`Resposta: ${respostaSelecionada}`);
            }

            if (observacao) {
                conteudo.push(`Observação: ${observacao}`);
            }

            documentos.push({
                titulo,
                conteudo,
            });
        });
    });

    return documentos;
};

const VisaoGeralTab = ({
    resposta,
    sindicatoData,
    documentos = [],
}: VisaoGeralTabProps) => {
    const respostaJson = useMemo<RespostaJson | null>(() => {
        try {
            const jsonString = resposta?.data?.json;
            if (!jsonString) return null;
            return JSON.parse(jsonString);
        } catch (error) {
            console.error("Erro ao fazer parse do JSON da resposta:", error);
            return null;
        }
    }, [resposta]);

    const sindicatoInfo = useMemo(
        () => ({
            cnpj: sindicatoData?.data?.cnpj || "Não informado",
            nome: sindicatoData?.data?.nome || "Não informado",
        }),
        [sindicatoData]
    );

    const documentosObrigatorios = useMemo(() => {
        if (!respostaJson?.docsObrigatorios) return [];
        return gerarMapasDeDocumentos(
            respostaJson.docsObrigatorios,
            documentos,
            respostaJson.selectedOptionsObrigatorios || {}
        );
    }, [documentos, respostaJson]);

    const documentosBonus = useMemo(() => {
        if (!respostaJson?.docsBonus) return [];
        return gerarMapasDeDocumentos(
            respostaJson.docsBonus,
            documentos,
            respostaJson.selectedOptionsBonus || {}
        );
    }, [documentos, respostaJson]);

    const documentosBonusExtra = useMemo(() => {
        if (!respostaJson?.docsBonusExtra) return [];
        return gerarMapasDeDocumentos(
            respostaJson.docsBonusExtra,
            documentos,
            respostaJson.selectedOptionsBonusExtra || {}
        );
    }, [documentos, respostaJson]);

    if (!resposta || !sindicatoData) {
        return <PageLoading minHeight="60vh" />;
    }

    return (
        <Container className="visao-geral-container">
            <h1 className="visao-geral-page-title">VISÃO GERAL DA DOCUMENTAÇÃO</h1>

            <section className="visao-geral-section">
                <h2 className="visao-geral-section-title">DADOS DO SINDICATO</h2>

                <div className="visao-geral-info-line">
                    <span>
                        <strong>CNPJ:</strong> {sindicatoInfo.cnpj}
                    </span>
                    <span>
                        <strong>Nome do Sindicato:</strong> {sindicatoInfo.nome}
                    </span>
                </div>
            </section>

            <Section
                titulo="DOCUMENTOS OBRIGATÓRIOS"
                documentos={documentosObrigatorios}
            />

            <Section titulo="DOCUMENTOS BÔNUS" documentos={documentosBonus} />

            <Section
                titulo="DOCUMENTOS BÔNUS EXTRA"
                documentos={documentosBonusExtra}
            />
        </Container>
    );
};

export default VisaoGeralTab;